import { IBotUserDisplayData, IUserDisplayData } from '@gem/models/userModel';
import { t } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { getGroupsByIds } from 'APP/model/group/groupModel';
import { GroupType, GroupUserRole, IPersonalizedGroup } from 'APP/model/group/groupModel.types';
import Entities from 'STORE';

import preloadGroupsAvatars from './preloadGroupsAvatars';

const privateGroupTypes: string[] = [
  GroupType.ChannelClosed,
  GroupType.P2P,
  GroupType.Closed,
  GroupType.MySavedMessages,
  GroupType.SecretGroup,
  GroupType.ServiceMessages,
];

async function getGroups(
  groupIds: string[],
  showAlert: boolean,
  isChannel: boolean
): Promise<IPersonalizedGroup[]> {
  try {
    if (!groupIds?.length) {
      return [];
    }

    const response = await getGroupsByIds(groupIds);

    if (groupIds.length === 1 && showAlert) {
      if ((response?.userBannedInGroups || []).includes(groupIds[0])) {
        Tasks.app.showAlert(t('banned_user_deeplink_error'));
        return [];
      }
    }

    if (response && response.items && response.items.length > 0) {
      return response.items;
    }

    if (showAlert && groupIds.length === 1) {
      Tasks.app.showAlert(
        isChannel ? t('common_channel_is_not_accessible') : t('common_chat_is_not_accessible')
      );
    }
  } catch (error) {
    return [];
  }

  return [];
}

function filteredPrivateGroupsWithoutMe(group: IPersonalizedGroup): boolean {
  if (
    privateGroupTypes.includes(group.groupResponse.type) &&
    group.userGroupRole === GroupUserRole.Guest
  ) {
    return false;
  }
  return true;
}

function getUsers(groups: IPersonalizedGroup[]): (IBotUserDisplayData | IUserDisplayData)[] {
  return groups.reduce((memo, group) => {
    const user = group.opponent || group.botOpponent;

    if (user) {
      memo.push(user);
    }

    return memo;
  }, [] as (IBotUserDisplayData | IUserDisplayData)[]);
}

/**
 *
 * @param groupIds
 * @param showAlert show alerts for groups if the user is banned or the group is not available.
 * Will work at the time of updating or loading information about the group
 * @param isChannel
 * @returns {Promise<void>}
 */
export async function loadGroupsByIds(
  groupIds: string[],
  showAlert = false,
  isChannel = false
): Promise<void> {
  const groups = await getGroups(groupIds, showAlert, isChannel);

  const filteredGroups = groups.filter(filteredPrivateGroupsWithoutMe);

  if (!filteredGroups.length) {
    return;
  }

  preloadGroupsAvatars(filteredGroups);

  const opponents = getUsers(filteredGroups);

  if (opponents.length > 0) {
    Entities.usersStore.add(opponents);
  }

  Entities.GroupsStore.merge(filteredGroups);

  const filteredSecretGroupIds = filteredGroups
    .filter((item) => item.groupResponse.type === GroupType.SecretGroup)
    .map((item) => item.groupResponse.id);

  await Tasks.e2ee.findSecretGroupsAndLoadWelcomes(filteredSecretGroupIds);
  const localGroups = filteredGroups.map((group) =>
    Entities.GroupsStore.getGroupById(group.groupResponse.id.toString())
  );

  await Tasks.relationships.loadUnknownDataFromGroups({ groups: localGroups });
}
